// src/components/workflows/nodes/WebhookNode.jsx - Webhook Action Node
import React, { useState } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Globe, Edit2, Code, Flag } from 'lucide-react';

/**
 * WebhookNode - Represents a webhook/API call action
 *
 * Node data structure (BACKEND-ALIGNED):
 * {
 *   id: 'webhook-uuid',
 *   type: 'action',           // ← Changed from 'webhook'
 *   data: {
 *     actionType: 'webhook',  // ← Backend checks this
 *     config: {               // ← Backend expects config object
 *       url: 'https://api.example.com/webhook',
 *       method: 'POST',
 *       headers: {},
 *       payload: {}
 *     },
 *     label: 'Notify External System',
 *     description: 'Notify external system'
 *   }
 * }
 */
const WebhookNode = ({ data, selected, id }) => {
  const {
    actionType = 'webhook',
    config = {},
    label,
    description,
    isStageGate = false,
    stageName = '',
    stageColor = 'purple'
  } = data;
  const url = config.url || '';
  const method = config.method || 'POST';
  const { updateNodeData } = useReactFlow();

  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [editedUrl, setEditedUrl] = useState(url);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description || '');

  // Save URL
  const handleSaveUrl = () => {
    updateNodeData(id, {
      ...data,
      config: { ...config, url: editedUrl.trim() }
    });
    setIsEditingUrl(false);
  };

  // Update method
  const handleMethodChange = (newMethod) => {
    updateNodeData(id, {
      ...data,
      config: { ...config, method: newMethod }
    });
  };

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  // Get method color
  const getMethodColor = () => {
    switch (method) {
      case 'POST': return 'text-green-400';
      case 'GET': return 'text-blue-400';
      case 'PUT': return 'text-yellow-400';
      case 'DELETE': return 'text-red-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <>
      {/* Input handle (left side) */}
      <Handle isConnectable={1}
        type="target"
        position={Position.Left}
        id="in"
        style={{ left: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-purple-500"
      />

      <div className={`
        w-72 rounded-lg border bg-slate-900
        ${selected ? 'ring-2 ring-purple-500 shadow-lg border-purple-500/50' : 'shadow-md border-purple-500/20'}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-purple-500/10 to-purple-600/5
        relative
      `}>
        {/* Stage gate indicator flag */}
        {isStageGate && (
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center shadow-lg z-10 border-2 border-slate-950">
            <Flag className="w-3.5 h-3.5 text-white" />
          </div>
        )}

        {/* Header */}
        <div className="p-3 pb-2 border-b border-purple-500/10">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-purple-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
                <Globe className="w-4 h-4 text-white" />
              </div>
              Webhook
            </div>
            <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-300">
              API
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2">
          {/* HTTP Method Selector */}
          <div className="flex items-center gap-2 nodrag">
            <Select value={method} onValueChange={handleMethodChange}>
              <SelectTrigger className="w-24 h-7 text-xs bg-slate-800 border-slate-700 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="POST" className="text-slate-200">POST</SelectItem>
                <SelectItem value="GET" className="text-slate-200">GET</SelectItem>
                <SelectItem value="PUT" className="text-slate-200">PUT</SelectItem>
                <SelectItem value="DELETE" className="text-slate-200">DELETE</SelectItem>
              </SelectContent>
            </Select>
            <span className={`text-xs font-mono font-bold ${getMethodColor()}`}>
              {method}
            </span>
          </div>

          {/* Editable URL */}
          {isEditingUrl ? (
            <Input
              value={editedUrl}
              onChange={(e) => setEditedUrl(e.target.value)}
              onBlur={handleSaveUrl}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveUrl();
                if (e.key === 'Escape') {
                  setEditedUrl(url);
                  setIsEditingUrl(false);
                }
              }}
              placeholder="https://api.example.com/webhook"
              autoFocus
              className="text-xs font-mono bg-slate-800 border-slate-700 text-slate-200 nodrag"
            />
          ) : (
            <div
              className="p-2 bg-slate-800/50 rounded border border-slate-700/50 cursor-pointer hover:bg-slate-800/70 transition-colors nodrag"
              onClick={() => setIsEditingUrl(true)}
            >
              {url ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-mono text-slate-300 truncate flex-1">
                    {url}
                  </div>
                  <Edit2 className="w-3 h-3 text-slate-500 flex-shrink-0" />
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-slate-500 italic">
                    Click to add URL
                  </div>
                  <Edit2 className="w-3 h-3 text-slate-500" />
                </div>
              )}
            </div>
          )}

          {/* Headers/Body Indicator */}
          <div className="flex items-center justify-center gap-3 py-2 text-xs text-slate-500 nodrag">
            <button className="flex items-center gap-1 text-purple-400 hover:text-purple-300">
              <Code className="w-3 h-3" />
              Configure payload
            </button>
          </div>

          {/* Editable Description */}
          <div className="border-t border-purple-500/10 pt-2">
            {isEditingDescription ? (
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDescription();
                  if (e.key === 'Escape') {
                    setEditedDescription(description || '');
                    setIsEditingDescription(false);
                  }
                }}
                placeholder="Add description..."
                autoFocus
                className="text-xs bg-slate-800 border-slate-700 text-slate-200 nodrag"
              />
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer nodrag"
                onClick={() => {
                  setEditedDescription(description || '');
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic text-center">
                    {description}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">
                    Add description...
                  </div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-purple-400" />
              </div>
            )}
          </div>

          {/* Stage Gate Toggle Section */}
          <div className="border-t border-purple-500/10 pt-3">
            <label className="flex items-center gap-2 cursor-pointer nodrag">
              <input
                type="checkbox"
                checked={isStageGate}
                onChange={(e) => updateNodeData(id, {
                  ...data,
                  isStageGate: e.target.checked,
                  stageName: e.target.checked && !stageName ? 'New Stage' : stageName
                })}
                className="w-4 h-4 text-purple-500 bg-slate-800 border-slate-600 rounded focus:ring-purple-500"
              />
              <span className="text-xs text-slate-300 font-medium">Make this a stage gate</span>
            </label>

            {isStageGate && (
              <div className="mt-2 space-y-2 nodrag">
                <Input
                  placeholder="Stage name (e.g., 'Webhook Sent')"
                  value={stageName}
                  onChange={(e) => updateNodeData(id, {
                    ...data,
                    stageName: e.target.value
                  })}
                  className="text-xs bg-slate-800/50 border-slate-700 text-slate-200"
                />
                <Select
                  value={stageColor}
                  onValueChange={(color) => updateNodeData(id, {
                    ...data,
                    stageColor: color
                  })}
                >
                  <SelectTrigger className="w-full bg-slate-800/50 border-slate-700 text-slate-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="purple" className="text-slate-200">Purple</SelectItem>
                    <SelectItem value="blue" className="text-slate-200">Blue</SelectItem>
                    <SelectItem value="green" className="text-slate-200">Green</SelectItem>
                    <SelectItem value="orange" className="text-slate-200">Orange</SelectItem>
                    <SelectItem value="red" className="text-slate-200">Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Output handle (right side) */}
      <Handle isConnectable={1}
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-purple-500"
      />
    </>
  );
};

export default WebhookNode;
